import React, { useState, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { useNavigate, useParams } from "react-router-dom";
import AuthUser from "../../AuthUser";
import axios from "axios";
import Global from "../../APIConfig";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaPrint, 
  FaCheck, 
  FaBox, 
  FaThLarge, 
  FaTh, 
  FaTag,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';
import './CourierBarcodeSystem.css';

const CourierBarcodeSystem = () => {
    let navigate = useNavigate();
    const { prs, custid } = useParams();
    const [text, setText] = useState([]);
    const [layout, setLayout] = useState('single_large');
    const containerRef = useRef(null);
    const { headers1, logout } = AuthUser();
    const [Loading, setLoading] = useState(true);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        GetPickupDetails();
    }, []);

    const GetPickupDetails = () => {
        axios({
            method: 'post',
            url: Global.baseUrl + 'GetAllAwbSchedulepickuplist',
            headers: headers1,
            data: {
                sp_id: prs,
                custid: custid
            }
        })
            .then(function (response) {
                if (response.data.status === 200) {
                    var prsdata = response.data.value;
                    setText(prsdata);
                }
                setLoading(false);
            })
            .catch(function (error) {
                setLoading(false);
                if (error.response?.status === 401) {
                    toast.error('Session expired. Please login again.');
                    logout();
                    navigate('/');
                } else {
                    toast.error('Failed to load pickup details');
                }
            });
    }

    const handleLayoutChange = (e) => {
        setLayout(e.target.value);
        setIsConfirmed(false);
    };

    const handlePrint = () => {
        window.print();
    };

    // Filter barcodes based on search term
    const filteredText = text.filter(item => 
        item.slip_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.awb_no?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Layout Configurations
    const getConfig = () => {
        switch (layout) {
            case 'single_large':
                return { 
                    width: 2.5, 
                    height: 80, 
                    fontSize: 16, 
                    cardW: '4in', 
                    cardH: '2in', 
                    cols: 1,
                    icon: <FaTag />,
                    label: 'Thermal (4" x 2")'
                };
            case 'medium_grid':
                return { 
                    width: 1.8, 
                    height: 50, 
                    fontSize: 12, 
                    cardW: '2.5in', 
                    cardH: '1.5in', 
                    cols: 3,
                    icon: <FaThLarge />,
                    label: 'Medium Grid'
                };
            case 'small_grid':
                return { 
                    width: 1.2, 
                    height: 35, 
                    fontSize: 10, 
                    cardW: '1.9in', 
                    cardH: '1in', 
                    cols: 4,
                    icon: <FaTh />,
                    label: 'Small Grid'
                };
            default:
                return {};
        }
    };

    const config = getConfig();

    // Barcode Generator
    useEffect(() => {
        if (!containerRef.current || !filteredText?.length) return;

        const svgs = containerRef.current.querySelectorAll('svg');

        svgs.forEach((svg, index) => {
            const value = filteredText[index]?.slip_no;

            if (!value || value === "") {
                svg.innerHTML = "";
                return;
            }

            try {
                JsBarcode(svg, String(value), {
                    format: "CODE128",
                    width: config.width,
                    height: config.height,
                    displayValue: true,
                    fontSize: config.fontSize,
                    margin: 5,
                    background: "#ffffff",
                    lineColor: "#000000"
                });
            } catch (err) {
                console.error("Barcode error:", err);
            }
        });
    }, [filteredText, layout]);

    if (Loading) {
        return (
            <div className="loading-container">
                <FaSpinner className="spinner1" />
                <p>Loading barcode data...</p>
            </div>
        );
    }

    return (
        <div className="barcode-system">
            <ToastContainer position="top-right" autoClose={3000} />
            
            {/* Header Section */}
            {/* <div className="header-section1">
                <div className="header-left1">
                    <h1>
                        <FaBox className="header-icon1" />
                        Courier Barcode System
                    </h1>
                    <div className="badge">
                        Total Items: {filteredText.length}
                    </div>
                </div>
            </div> */}

            {/* Control Panel */}
            <div className="control-panel1 no-print">
                <div className="panel-left1">
                    <div className="search-box1">
                        <input
                            type="text"
                            placeholder="Search by Slip No or AWB..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input1"
                        />
                    </div>

                    <div className="layout-selector1">
                        <label>Layout:</label>
                        <div className="layout-options1">
                            {['single_large', 'medium_grid', 'small_grid'].map((layoutType) => {
                                const layoutConfig = layoutType === 'single_large' 
                                    ? { icon: <FaTag />, label: 'Thermal' }
                                    : layoutType === 'medium_grid'
                                    ? { icon: <FaThLarge />, label: 'Medium' }
                                    : { icon: <FaTh />, label: 'Small' };
                                
                                return (
                                    <button
                                        key={layoutType}
                                        className={`layout-btn1 ${layout === layoutType ? 'active' : ''}`}
                                        onClick={() => {
                                            setLayout(layoutType);
                                            setIsConfirmed(false);
                                        }}
                                    >
                                        {layoutConfig.icon}
                                        <span>{layoutConfig.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="panel-right1">
                        <button
                            className="print-btn1"
                            onClick={handlePrint}
                        >
                            <FaPrint />
                            Print Now
                        </button>
                </div>
            </div>

            {/* Stats Bar */}
            {isConfirmed && (
                <div className="stats-bar1 no-print">
                    <div className="stat-item">
                        <span className="stat-label">Layout:</span>
                        <span className="stat-value">{config.label}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Items to Print:</span>
                        <span className="stat-value">{filteredText.length}</span>
                    </div>
                </div>
            )}

            {/* Barcode Area */}
            {filteredText.length > 0 ? (
                <div
                    ref={containerRef}
                    className={`print-container layout-${layout}`}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${config.cols}, 1fr)`,
                        gap: layout === 'single_large' ? '5px' : '10px',
                        padding: '10px',
                        background: '#fafafa',
                        borderRadius: '8px'
                    }}
                >
                    {filteredText.map((item, i) => (
                        <div
                            key={i}
                            className={`label-card1 ${layout === 'single_large' ? 'thermal' : ''}`}
                            style={{
                                width: config.cardW,
                                height: config.cardH,
                                border: '1px solid #e0e0e0',
                                borderRadius: layout === 'single_large' ? '4px' : '6px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                padding: '8px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {item.awb_no && (
                                <div className="awb-number" style={{
                                    fontSize: '10px',
                                    color: '#666',
                                    marginBottom: '4px',
                                    fontWeight: '500'
                                }}>
                                    AWB: {item.awb_no}
                                </div>
                            )}
                            <svg style={{ maxWidth: '100%' }}></svg>
                            {item.shipment_type && (
                                <div className="shipment-type" style={{
                                    fontSize: '9px',
                                    color: '#999',
                                    marginTop: '4px',
                                    textTransform: 'uppercase'
                                }}>
                                    {item.shipment_type}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <FaExclamationTriangle className="empty-icon" />
                    <h3>No barcodes to display</h3>
                    <p>Try adjusting your search or check back later</p>
                </div>
            )}

            {/* Print Styles */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { margin: 0; padding: 0; background: white; }
                    
                    .print-container {
                        background: white !important;
                        padding: 0 !important;
                        gap: 2px !important;
                    }
                    
                    .label-card1 {
                        page-break-inside: avoid;
                        break-inside: avoid;
                        border: none !important;
                        box-shadow: none !important;
                        background: white !important;
                    }
                    
                    .thermal {
                        page-break-after: always;
                    }
                    
                    .awb-number, .shipment-type {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default CourierBarcodeSystem;